import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useDrawerOverflow } from './drawerContext';

// ========== Styled Components ==========

const DrawerOverlay = styled.div<{ $open: boolean; $bg: string; $skipAnimation?: boolean; $zIndex?: number }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(p) => p.$bg};
    z-index: ${(p) => p.$zIndex ?? 30};
    opacity: ${(p) => (p.$open ? 1 : 0)};
    visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
    ${(p) =>
        !p.$skipAnimation &&
        `
		transition: opacity 0.3s ease, visibility 0.3s ease;
		transition-behavior: allow-discrete;
	`}

    @starting-style {
        opacity: 0;
        visibility: hidden;
    }
`;

const fullScreenFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(0) scale(0.99);
    }

    to {
        opacity: 1;
        transform: translateX(0) scale(1);
    }
`;

const fullScreenFadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateX(0) scale(1);
    }

    to {
        opacity: 0;
        transform: translateX(0) scale(0.985);
    }
`;

const DrawerPanel = styled.div<{
    $open: boolean;
    $width: number;
    $dimmed?: boolean;
    $skipAnimation?: boolean;
    $fullScreen?: boolean;
    $isResizing?: boolean;
    $fullScreenTransition: 'resize' | 'fade' | 'none';
    $animateFullScreenEntry: boolean;
    $isFullScreenExiting: boolean;
    $zIndex?: number;
}>`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: ${(p) => (p.$fullScreen ? '0' : 'auto')};
    width: ${(p) => (p.$fullScreen ? '100%' : `${p.$width}px`)};
    max-width: 100%;
    background: ${(p) => (p.$fullScreen ? '#f5f7fa' : 'white')};
    z-index: ${(p) => (p.$zIndex ?? 30) + 1};
    visibility: ${(p) => (!p.$open && p.$fullScreenTransition === 'fade' ? 'hidden' : 'visible')};
    transform: translateX(${(p) => (p.$open ? '0' : '100%')});
    transform-origin: center;
    ${(p) =>
        !p.$skipAnimation &&
        !p.$isResizing &&
        (p.$fullScreen && p.$fullScreenTransition !== 'resize'
            ? 'transition: background 0.18s ease, box-shadow 0.18s ease;'
            : 'transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), left 0.35s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease, box-shadow 0.3s ease;')}
    ${(p) =>
        !p.$skipAnimation &&
        !p.$fullScreen &&
        css`
            /* Slide in even when the drawer is mounted already-open (e.g. a stacked AI drawer). */
            @starting-style {
                transform: translateX(100%);
            }
        `}
    ${(p) =>
        !p.$skipAnimation &&
        p.$open &&
        p.$fullScreen &&
        p.$fullScreenTransition === 'fade' &&
        p.$animateFullScreenEntry &&
        !p.$isFullScreenExiting &&
        css`
            animation: ${fullScreenFadeIn} 180ms cubic-bezier(0.2, 0, 0, 1);

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        `}
    ${(p) =>
        !p.$skipAnimation &&
        p.$open &&
        p.$fullScreen &&
        p.$fullScreenTransition === 'fade' &&
        p.$isFullScreenExiting &&
        css`
            animation: ${fullScreenFadeOut} 220ms cubic-bezier(0.4, 0, 1, 1) forwards;
            pointer-events: none;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        `}
    display: flex;
    flex-direction: column;
    box-shadow: ${(p) => (p.$fullScreen ? 'none' : '-4px 0 24px rgba(0, 0, 0, 0.12)')};
    border-left: ${(p) => (p.$fullScreen ? 'none' : '1px solid #e5e7eb')};
    ${(p) => p.$dimmed && 'filter: brightness(0.95); pointer-events: none;'}

    ${(p) =>
        !p.$skipAnimation &&
        `@starting-style {
		transform: translateX(100%);
	}`}
`;

const DrawerResizeHandle = styled.div`
    position: absolute;
    top: 0;
    left: -4px;
    bottom: 0;
    width: 12px;
    cursor: col-resize;
    z-index: 1;

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 3px;
        transform: translateY(-50%);
        width: 5px;
        height: 60px;
        border-radius: 4px;
        background: #b0b0b0;
        transition: background 0.2s ease;
    }

    &:hover::after {
        background: #666;
    }
`;

const DrawerHeaderBar = styled.div<{ $hasCustomHeader: boolean; $fullScreen?: boolean }>`
    display: flex;
    align-items: ${(p) => (p.$hasCustomHeader ? 'flex-start' : 'center')};
    justify-content: space-between;
    padding: ${(p) => (p.$hasCustomHeader ? '0' : p.$fullScreen ? '16px 32px' : '16px 24px')};
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    gap: 8px;
    ${(p) => p.$hasCustomHeader && 'position: relative;'}
    ${(p) => p.$fullScreen && 'background: white;'}
	transition: padding 0.3s ease;
`;

const DrawerHeaderActions = styled.div<{ $hasCustomHeader: boolean; $fullScreen?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    ${(p) =>
        p.$hasCustomHeader &&
        `
		position: absolute;
		top: 12px;
		right: ${p.$fullScreen ? '16px' : '12px'};
		z-index: 1;
	`}
`;

const DrawerHeaderContent = styled.div<{ $hasCustomHeader: boolean }>`
    flex: 1;
    min-width: 0;
    ${(p) => p.$hasCustomHeader && 'width: 100%;'}
`;

const DrawerTitle = styled.h3`
    margin: 0;
    font-size: ${(p) => p.theme.app.component.card.title.size};
    font-weight: ${(p) => p.theme.app.component.card.title.weight};
    color: ${(p) => p.theme.app.component.card.title.color};
    font-family: ${(p) => p.theme.app.font};
`;

const DrawerSubtitle = styled.div`
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
    font-weight: 400;
`;

const DrawerActionButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    cursor: pointer;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
`;

const DrawerCloseButton = styled(DrawerActionButton)`
    font-size: 18px;
`;

const DrawerExpandButton = styled(DrawerActionButton)`
    font-size: 14px;
`;

const DrawerBackButton = styled(DrawerActionButton)`
    font-size: 16px;
`;

const DrawerContent = styled.div`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    container-type: inline-size;
`;

const DrawerFooterBar = styled.div<{ $hidden?: boolean }>`
    border-top: 1px solid #e5e7eb;
    flex-shrink: 0;
    ${(p) =>
        p.$hidden &&
        `
		max-height: 0;
		overflow: hidden;
		border-top: none;
		opacity: 0;
	`}
    transition: max-height 0.3s ease, opacity 0.3s ease;
`;

// ========== Fullscreen ==========

const FullscreenOverlay = styled.div<{ $visible: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.08);
    z-index: 30;
    opacity: ${(p) => (p.$visible ? 1 : 0)};
    visibility: ${(p) => (p.$visible ? 'visible' : 'hidden')};
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
`;

// ========== Props ==========

export interface DrawerProps {
    /** Controls open/close state */
    isOpen: boolean;
    /** Called when user clicks overlay or close button */
    onClose: () => void;
    /** Header content: string renders a title; { title, subtitle } renders title + subtitle; ReactNode renders custom header */
    header: string | { title: string; subtitle?: string | ReactNode } | ReactNode;
    /** Extra action buttons rendered before the expand/close buttons */
    headerActions?: ReactNode;
    /** Panel body. Supports render-function for fullscreen-aware content */
    children: ReactNode | ((info: { isFullScreen: boolean }) => ReactNode);
    /** Optional footer slot (hidden in fullscreen mode) */
    footer?: ReactNode;
    /** Enable drag-to-resize handle on left edge. Default: false */
    isResizable?: boolean;
    /** Panel width in pixels. Default: 620 */
    width?: number;
    /** Minimum width when resizable. Default: width - 40 */
    minWidth?: number;
    /** Visual dimming when stacked panels overlay this one. Default: false */
    isDimmed?: boolean;
    /** Overlay background color. Default: 'rgba(0, 0, 0, 0.15)' */
    overlayBackground?: string;
    /** Show a fullscreen/expand toggle button in the header. Default: false */
    showFullScreenButton?: boolean;
    /** Show the close button in the header. Default: true */
    showCloseButton?: boolean;
    /** Controlled fullscreen state. Omit to let the Drawer manage it internally. */
    isFullScreen?: boolean;
    /** Called whenever the fullscreen state is changed from the Drawer controls. */
    onFullScreenChange?: (isFullScreen: boolean) => void;
    /** Fullscreen mode transition. Default: resize. */
    fullScreenTransition?: 'resize' | 'fade' | 'none';
    /** Whether the fullscreen entry transition should run. Default: true. */
    animateFullScreenEntry?: boolean;
    /** Whether the fullscreen exit transition is currently running. Default: false. */
    isFullScreenExiting?: boolean;
    /** Header content to show in fullscreen mode. Falls back to `header` if not provided */
    fullScreenHeader?: string | { title: string; subtitle?: string | ReactNode } | ReactNode;
    /** Skip slide-in animation (appear instantly). Default: false */
    skipAnimation?: boolean;
    /** Stacked drawer: shows back button instead of close, hides overlay. Default: false */
    isStacked?: boolean;
    /** Base z-index (overlay uses it, panel uses it + 1). Raise it to stack this drawer above another open drawer. Default: 30 */
    zIndex?: number;
    /** Additional className on the panel container */
    className?: string;
}

// ========== Component ==========

const DEFAULT_WIDTH = 620;
// A resizable drawer can grow to the viewport width minus this left gap (matching the full-screen reach).
const RESIZE_LEFT_GAP = 80;

export const Drawer: FC<DrawerProps> = ({
    isOpen,
    onClose,
    header,
    headerActions,
    children,
    footer,
    isResizable = false,
    width = DEFAULT_WIDTH,
    minWidth,
    isDimmed = false,
    overlayBackground = 'rgba(0, 0, 0, 0.15)',
    showFullScreenButton = false,
    showCloseButton = true,
    isFullScreen: controlledIsFullScreen,
    onFullScreenChange,
    fullScreenTransition = 'resize',
    animateFullScreenEntry = true,
    isFullScreenExiting = false,
    fullScreenHeader,
    skipAnimation = false,
    isStacked = false,
    zIndex,
    className
}) => {
    // Resize can only widen: the drawer never shrinks below its starting width.
    const resolvedMinWidth = minWidth ?? width;
    const [panelWidth, setPanelWidth] = useState(width);
    const [internalIsFullScreen, setInternalIsFullScreen] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const isResizingRef = useRef(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(width);

    useDrawerOverflow(isOpen, isStacked);

    const isFullScreen = controlledIsFullScreen ?? internalIsFullScreen;
    const setIsFullScreen = (value: boolean) => {
        if (controlledIsFullScreen === undefined) setInternalIsFullScreen(value);
        onFullScreenChange?.(value);
    };

    const resolveHeaderParts = (h: DrawerProps['header']) => {
        const isStructured = typeof h === 'object' && h !== null && !React.isValidElement(h) && 'title' in h;
        const structured = isStructured ? (h as { title: string; subtitle?: string | ReactNode }) : null;
        const isCustom = typeof h !== 'string' && !isStructured;
        return { isStructured, structured, isCustom };
    };

    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!isResizable) return;
            e.preventDefault();
            isResizingRef.current = true;
            setIsResizing(true);
            startXRef.current = e.clientX;
            startWidthRef.current = panelWidth;

            const handleMouseMove = (ev: MouseEvent) => {
                if (!isResizingRef.current) return;
                const delta = startXRef.current - ev.clientX;
                // Cap the expansion where the full-screen drawer reaches, leaving a little space on the left.
                const maxResizeWidth = window.innerWidth - RESIZE_LEFT_GAP;
                const newWidth = Math.min(maxResizeWidth, Math.max(resolvedMinWidth, startWidthRef.current + delta));
                setPanelWidth(newWidth);
            };

            const handleMouseUp = () => {
                isResizingRef.current = false;
                setIsResizing(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };

            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [isResizable, panelWidth, resolvedMinWidth]
    );

    const currentWidth = isResizable ? panelWidth : width;

    const renderHeaderContent = (h: DrawerProps['header']): ReactNode => {
        const { isCustom, structured } = resolveHeaderParts(h);
        if (isCustom) return h as ReactNode;
        if (structured) {
            return (
                <>
                    <DrawerTitle>{structured.title}</DrawerTitle>
                    {structured.subtitle && <DrawerSubtitle>{structured.subtitle}</DrawerSubtitle>}
                </>
            );
        }
        return <DrawerTitle>{h as ReactNode}</DrawerTitle>;
    };

    const renderChildren = () => {
        if (typeof children === 'function') {
            return children({ isFullScreen });
        }
        return children;
    };

    const activeHeader = isFullScreen ? (fullScreenHeader ?? header) : header;
    const { isCustom: activeHasCustomHeader } = resolveHeaderParts(activeHeader);

    return (
        <>
            {!isStacked && (
                <DrawerOverlay $open={isOpen && !isFullScreen} $bg={overlayBackground} $skipAnimation={skipAnimation} $zIndex={zIndex} onClick={onClose} />
            )}
            <FullscreenOverlay $visible={isOpen && isFullScreen} onClick={() => setIsFullScreen(false)} />
            <DrawerPanel
                $open={isOpen}
                $fullScreen={isFullScreen}
                $width={currentWidth}
                $dimmed={isDimmed}
                $skipAnimation={skipAnimation}
                $isResizing={isResizing}
                $fullScreenTransition={fullScreenTransition}
                $animateFullScreenEntry={animateFullScreenEntry}
                $isFullScreenExiting={isFullScreenExiting}
                $zIndex={zIndex}
                className={className}
            >
                {isResizable && !isFullScreen && <DrawerResizeHandle onMouseDown={handleResizeMouseDown} />}
                <DrawerHeaderBar $hasCustomHeader={activeHasCustomHeader} $fullScreen={isFullScreen}>
                    {isStacked && !isFullScreen && (
                        <DrawerBackButton onClick={onClose} title='Back'>
                            &#8592;
                        </DrawerBackButton>
                    )}
                    <DrawerHeaderContent $hasCustomHeader={activeHasCustomHeader}>{renderHeaderContent(activeHeader)}</DrawerHeaderContent>
                    <DrawerHeaderActions $hasCustomHeader={activeHasCustomHeader} $fullScreen={isFullScreen}>
                        {headerActions}
                        {showFullScreenButton && (
                            <DrawerExpandButton onClick={() => setIsFullScreen(!isFullScreen)} title={isFullScreen ? 'Collapse' : 'Expand'}>
                                {isFullScreen ? (
                                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <path d='M12.5 5.5H8.5V1.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                        <path d='M8.5 5.5L13 1' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                                        <rect x='1.5' y='4.5' width='8' height='8' rx='1' stroke='currentColor' strokeWidth='1.5' />
                                    </svg>
                                ) : (
                                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <path d='M8.5 1.5H12.5V5.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                        <path d='M12.5 1.5L7.5 6.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
                                        <rect x='1.5' y='4.5' width='8' height='8' rx='1' stroke='currentColor' strokeWidth='1.5' />
                                    </svg>
                                )}
                            </DrawerExpandButton>
                        )}
                        {showCloseButton && !isStacked && <DrawerCloseButton onClick={onClose}>&times;</DrawerCloseButton>}
                    </DrawerHeaderActions>
                </DrawerHeaderBar>
                <DrawerContent>{renderChildren()}</DrawerContent>
                {footer && <DrawerFooterBar $hidden={isFullScreen}>{footer}</DrawerFooterBar>}
            </DrawerPanel>
        </>
    );
};
